import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercentage,
} from '@/utils/helpers';
import type { LoanDetailResponse, LoanResponse } from '@/types/api.types';

interface OverviewTabProps {
  loan: LoanResponse;
  loanExtraData: LoanDetailResponse | null;
}

export function OverviewTab({ loan, loanExtraData }: OverviewTabProps) {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    basic: true,
    financial: true,
    property: true,
    dates: true,
  });

  function toggleSection(section: string) {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  const propertyLocation = loanExtraData?.property_details?.locations?.[0];
  const propertyLocationAddress =
    [propertyLocation?.street, propertyLocation?.city, propertyLocation?.state]
      .filter(Boolean)
      .join(', ') || '-';

  return (
    <div className="space-y-6" data-test-id="overview-tab">
      {/* Basic Information */}
      <div
        className="bg-white rounded-lg shadow p-6"
        data-test-id="basic-info-section"
      >
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('basic')}
          data-test-id="basic-info-header"
        >
          <h2 className="text-xl font-semibold">Loan Information</h2>
          {expandedSections.basic ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.basic && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <div data-test-id="loan-name-field">
              <p className="text-sm text-gray-600">Loan Name</p>
              <p className="font-medium">{loan.loan_name || '-'}</p>
            </div>
            <div data-test-id="borrower-field">
              <p className="text-sm text-gray-600">Borrower</p>
              <p className="font-medium">{loan.sponsor_borrower || '-'}</p>
            </div>
            <div data-test-id="property-type-field">
              <p className="text-sm text-gray-600">Property Type</p>
              <p className="font-medium">{loan.property_sector || '-'}</p>
            </div>
            <div data-test-id="currency-field">
              <p className="text-sm text-gray-600">Currency</p>
              <p className="font-medium">{loan.currency || 'USD'}</p>
            </div>
            <div data-test-id="interest-type-field">
              <p className="text-sm text-gray-600">Interest Type</p>
              <p className="font-medium">{loan.interest_type || '-'}</p>
            </div>
            <div data-test-id="amortization-field">
              <p className="text-sm text-gray-600">Amortization</p>
              <p className="font-medium">{loan.amortization_type || '-'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Financial Metrics */}
      <div
        className="bg-white rounded-lg shadow p-6"
        data-test-id="financial-section"
      >
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('financial')}
          data-test-id="financial-header"
        >
          <h2 className="text-xl font-semibold">Loan Pricing</h2>
          {expandedSections.financial ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.financial && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <div data-test-id="original-balance-field">
              <p className="text-sm text-gray-600">Original Balance</p>
              <p className="font-medium">
                {formatCurrency(loan.original_balance, 2, loan.currency || 'USD')}
              </p>
            </div>
            <div data-test-id="current-balance-field">
              <p className="text-sm text-gray-600">Current Balance</p>
              <p className="font-medium">
                {formatCurrency(loan.current_balance, 2, loan.currency || 'USD')}
              </p>
            </div>
            <div data-test-id="fair-value-field">
              <p className="text-sm text-gray-600">Fair Value</p>
              <p className="font-medium">
                {formatCurrency(loanExtraData?.pricing_results?.fair_value, 2, loan.currency || 'USD')}
              </p>
            </div>
            <div data-test-id="price-field">
              <p className="text-sm text-gray-600">Price</p>
              <p className="font-medium">
                {formatPercentage(
                  Number(loanExtraData?.pricing_results?.price) * 100,
                  5
                )}
              </p>
            </div>
            <div data-test-id="market-yield-field">
              <p className="text-sm text-gray-600">Market Yield</p>
              <p className="font-medium">
                {formatPercentage(
                  Number(loanExtraData?.pricing_results?.market_yield) * 100,
                  3
                )}
              </p>
            </div>
            <div data-test-id="modified-duration-field">
              <p className="text-sm text-gray-600">Modified Duration</p>
              <p className="font-medium">
                {loanExtraData?.pricing_results?.modified_duration != null
                  ? `${formatNumber(loanExtraData.pricing_results.modified_duration, 2)} years`
                  : '-'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Property Information */}
      <div
        className="bg-white rounded-lg shadow p-6"
        data-test-id="property-section"
      >
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('property')}
          data-test-id="property-header"
        >
          <h2 className="text-xl font-semibold">Property Information</h2>
          {loanExtraData?.property_details ? <ChevronUp /> : <ChevronDown />}
        </div>
        {propertyLocation && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div data-test-id="property-address-field">
              <p className="text-sm text-gray-600">Address</p>
              <p className="font-medium">{propertyLocationAddress}</p>
            </div>
            <div data-test-id="property-country-field">
              <p className="text-sm text-gray-600">Country</p>
              <p className="font-medium">{propertyLocation.country || '-'}</p>
            </div>
            <div data-test-id="property-sector-field">
              <p className="text-sm text-gray-600">Sector</p>
              <p className="font-medium">{loan.property_sector || '-'}</p>
            </div>
            <div data-test-id="property-region-field">
              <p className="text-sm text-gray-600">Region</p>
              <p className="font-medium">{propertyLocation.region || '-'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Key Dates */}
      <div
        className="bg-white rounded-lg shadow p-6"
        data-test-id="dates-section"
      >
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('dates')}
          data-test-id="dates-header"
        >
          <h2 className="text-xl font-semibold">Key Dates</h2>
          {expandedSections.dates ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.dates && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <div>
              <div data-test-id="origination-date-field">
                <p className="text-sm text-gray-600">Origination Date</p>
                <p className="font-medium">{formatDate(loan.origination_date)}</p>
              </div>
              <div data-test-id="first-extension-date-field" className="mt-3">
                <p className="text-sm text-gray-600">1st Extension Date</p>
                <p className="font-medium">
                  {formatDate(loan.first_extension_date)}
                </p>
              </div>
            </div>
            <div>
              <div data-test-id="first-payment-field">
                <p className="text-sm text-gray-600">First Payment</p>
                <p className="font-medium">
                  {formatDate(loan.first_payment_date)}
                </p>
              </div>
              <div data-test-id="second-extension-date-field" className="mt-3">
                <p className="text-sm text-gray-600">2nd Extension Date</p>
                <p className="font-medium">
                  {formatDate(loan.second_extension_date)}
                </p>
              </div>
            </div>
            <div>
              <div data-test-id="maturity-date-field">
                <p className="text-sm text-gray-600">Maturity Date</p>
                <p className="font-medium">
                  {formatDate(loan.original_maturity_date)}
                </p>
              </div>
              <div data-test-id="third-extension-date-field" className="mt-3">
                <p className="text-sm text-gray-600">3rd Extension Date</p>
                <p className="font-medium">
                  {formatDate(loan.third_extension_date)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
